/**
 * GalleryView.tsx - GalleryView - Pinterest/Notion-style visual card grid
 * 
 * CHANGE HISTORY:
 * - 251021_0421 [7529d5e] danielbeer: feat: migrate all packages to TypeScript structure
 */
import React, { useState, useCallback, useMemo, useRef } from 'react';
import { useDatasetView } from '../base/useDatasetView';
import { useViewMetadata } from '../base/useViewMetadata';
import { useBacktrackr } from '@opcentral/backtrackr';
import GalleryCard from './GalleryCard';
import GalleryModal from './GalleryModal';
import './GalleryView.css';

/**
 * GalleryView Component
 * 
 * @param {Object} props
 * @param {string} props.viewId - Unique view identifier
 * @param {Object} props.datasetContext - Dataset context
 * @param {Object} props.dataContext - Data context
 * @param {Object} props.config - View configuration
 */
const GalleryView = ({
  viewId = 'gallery-default',
  datasetContext,
  dataContext,
  backtrackrContext: propBacktrackrContext,
  config = {}
}) => {
  // State
  const [selectedItemId, setSelectedItemId] = useState(null);
  const [layout, setLayout] = useState('masonry'); // masonry, grid, list
  const [cardSize, setCardSize] = useState('medium'); // small, medium, large
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');
  const [filterTag, setFilterTag] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showModal, setShowModal] = useState(false);
  
  const galleryRef = useRef(null);
  
  // Contexts
  const {
    items,
    fieldMappings,
    getField,
    updateItem,
    addItem,
    deleteItem
  } = useDatasetView('gallery', datasetContext, dataContext);
  
  const hookBacktrackrContext = useBacktrackr();
  const backtrackrContext = propBacktrackrContext || hookBacktrackrContext;
  const { recordChange, undo, redo, canUndo, canRedo } = backtrackrContext || {};
  
  const {
    getMetadata,
    setMetadata,
    getSortOrder,
    setSortOrder: setViewSortOrder
  } = useViewMetadata(viewId, items, updateItem);
  
  // Extract config
  const galleryConfig = config.gallery || {};
  const showTags = galleryConfig.showTags !== false;
  const showDates = galleryConfig.showDates !== false;
  const showDescriptions = galleryConfig.showDescriptions !== false;
  const columnsCount = galleryConfig.columns || 4;
  
  // Get all tags
  const allTags = useMemo(() => {
    const tagSet = new Set();
    items.forEach(item => {
      const tags = getField(item, 'tags');
      if (Array.isArray(tags)) {
        tags.forEach(tag => tagSet.add(tag));
      }
    });
    return Array.from(tagSet).sort();
  }, [items, getField]);
  
  // Filter and sort items
  const filteredItems = useMemo(() => {
    let filtered = [...items];
    
    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(item => {
        const name = (getField(item, 'name') || '').toLowerCase();
        const description = (getField(item, 'description') || '').toLowerCase();
        return name.includes(query) || description.includes(query);
      });
    }
    
    // Tag filter
    if (filterTag) {
      filtered = filtered.filter(item => {
        const tags = getField(item, 'tags');
        return Array.isArray(tags) && tags.includes(filterTag);
      });
    }
    
    // Sort
    filtered.sort((a, b) => {
      let aVal, bVal;
      
      if (sortBy === 'viewOrder') {
        aVal = getSortOrder(a.id) ?? 999999;
        bVal = getSortOrder(b.id) ?? 999999;
      } else {
        aVal = getField(a, sortBy);
        bVal = getField(b, sortBy);
      }
      
      if (aVal === bVal) return 0;
      
      const comparison = aVal < bVal ? -1 : 1;
      return sortOrder === 'asc' ? comparison : -comparison;
    });
    
    return filtered;
  }, [items, searchQuery, filterTag, sortBy, sortOrder, getField, getSortOrder]);
  
  // Handle card click
  const handleCardClick = useCallback((itemId) => {
    setSelectedItemId(itemId);
    setShowModal(true);
  }, []);
  
  // Handle create new item
  const handleCreateItem = useCallback(() => {
    const nameField = fieldMappings['name'];
    if (!nameField) return;
    
    const newItem = {
      [nameField]: 'New Item'
    };
    
    const newId = addItem(newItem);
    if (newId) {
      setSelectedItemId(newId);
      setShowModal(true);
    }
  }, [fieldMappings, addItem]);
  
  // Handle delete item
  const handleDeleteItem = useCallback((itemId) => {
    if (window.confirm('Are you sure you want to delete this item?')) {
      deleteItem(itemId);
      if (selectedItemId === itemId) {
        setSelectedItemId(null);
        setShowModal(false);
      }
    }
  }, [deleteItem, selectedItemId]);
  
  // Handle card drag (reorder)
  const handleCardDragStart = useCallback((e, itemId) => {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', itemId);
  }, []);
  
  const handleCardDrop = useCallback((e, targetItemId) => {
    e.preventDefault();
    const draggedItemId = e.dataTransfer.getData('text/plain');
    
    if (draggedItemId === targetItemId) return;
    
    // Reorder items
    const draggedIndex = filteredItems.findIndex(item => item.id === draggedItemId);
    const targetIndex = filteredItems.findIndex(item => item.id === targetItemId);
    
    if (draggedIndex === -1 || targetIndex === -1) return;
    
    // Update sort orders
    const reordered = [...filteredItems];
    const [removed] = reordered.splice(draggedIndex, 1);
    reordered.splice(targetIndex, 0, removed);
    
    // Batch update sort orders
    reordered.forEach((item, index) => {
      setViewSortOrder(item.id, index);
    });
    
    // Switch to view order sort
    setSortBy('viewOrder');
  }, [filteredItems, setViewSortOrder]);
  
  // Get card dimensions based on size
  const getCardDimensions = useCallback(() => {
    switch (cardSize) {
      case 'small':
        return { width: 200, minHeight: 200 };
      case 'large':
        return { width: 400, minHeight: 300 };
      default: // medium
        return { width: 280, minHeight: 240 };
    }
  }, [cardSize]);
  
  const cardDimensions = getCardDimensions();
  
  // Calculate columns for grid layout
  const gridColumns = useMemo(() => {
    if (layout === 'list') return 1;
    if (layout === 'grid') return columnsCount;
    return 'auto'; // masonry
  }, [layout, columnsCount]);
  
  return (
    <div className="gallery-view" ref={galleryRef}>
      {/* Toolbar */}
      <div className="gallery-toolbar">
        <div className="gallery-toolbar-left">
          <button onClick={handleCreateItem} className="btn-new-item">
            + New Item
          </button>
          
          <input
            type="text"
            placeholder="Search..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="gallery-search-input"
          />
          
          {showTags && allTags.length > 0 && (
            <select
              value={filterTag || ''}
              onChange={(e) => setFilterTag(e.target.value || null)}
              className="gallery-filter-select"
            >
              <option value="">All Tags</option>
              {allTags.map(tag => (
                <option key={tag} value={tag}>{tag}</option>
              ))}
            </select>
          )}
        </div>
        
        <div className="gallery-toolbar-right">
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="gallery-sort-select"
          >
            <option value="viewOrder">Manual Order</option>
            <option value="name">Name</option>
            <option value="createdAt">Created</option>
            <option value="updatedAt">Updated</option>
          </select>
          
          <button
            onClick={() => setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc')}
            className="btn-sort-order"
            title={sortOrder === 'asc' ? 'Ascending' : 'Descending'}
          >
            {sortOrder === 'asc' ? '↑' : '↓'}
          </button>
          
          <div className="gallery-layout-buttons">
            <button
              className={`btn-layout ${layout === 'masonry' ? 'active' : ''}`}
              onClick={() => setLayout('masonry')}
              title="Masonry"
            >
              ⊞
            </button>
            <button
              className={`btn-layout ${layout === 'grid' ? 'active' : ''}`}
              onClick={() => setLayout('grid')}
              title="Grid"
            >
              ⊟
            </button>
            <button
              className={`btn-layout ${layout === 'list' ? 'active' : ''}`}
              onClick={() => setLayout('list')}
              title="List"
            >
              ☰
            </button>
          </div>
          
          <select
            value={cardSize}
            onChange={(e) => setCardSize(e.target.value)}
            className="gallery-size-select"
          >
            <option value="small">Small</option>
            <option value="medium">Medium</option>
            <option value="large">Large</option>
          </select>
        </div>
      </div>
      
      {/* Gallery Grid */}
      <div
        className={`gallery-grid layout-${layout}`}
        style={{
          gridTemplateColumns: layout === 'grid' 
            ? `repeat(${gridColumns}, 1fr)` 
            : layout === 'list'
            ? '1fr'
            : `repeat(auto-fill, minmax(${cardDimensions.width}px, 1fr))`
        }}
      >
        {filteredItems.map((item) => (
          <GalleryCard
            key={item.id}
            item={item}
            getField={getField}
            dimensions={cardDimensions}
            layout={layout}
            showTags={showTags}
            showDates={showDates}
            showDescriptions={showDescriptions}
            onClick={() => handleCardClick(item.id)}
            onDelete={() => handleDeleteItem(item.id)}
            onDragStart={(e) => handleCardDragStart(e, item.id)}
            onDrop={(e) => handleCardDrop(e, item.id)}
            onDragOver={(e) => e.preventDefault()}
          />
        ))}
      </div>
      
      {/* Empty state */}
      {filteredItems.length === 0 && (
        <div className="gallery-empty-state">
          <h3>No items found</h3>
          <p>
            {searchQuery || filterTag 
              ? 'Try adjusting your filters'
              : 'Create your first item to get started'
            }
          </p>
          {!searchQuery && !filterTag && (
            <button onClick={handleCreateItem} className="btn-create-first">
              Create First Item
            </button>
          )}
        </div>
      )}
      
      {/* Modal */}
      {showModal && selectedItemId && (
        <GalleryModal
          item={items.find(i => i.id === selectedItemId)}
          getField={getField}
          updateItem={updateItem}
          fieldMappings={fieldMappings}
          onClose={() => setShowModal(false)}
          onDelete={() => handleDeleteItem(selectedItemId)}
          onNext={() => {
            const currentIndex = filteredItems.findIndex(i => i.id === selectedItemId);
            const nextIndex = (currentIndex + 1) % filteredItems.length;
            setSelectedItemId(filteredItems[nextIndex].id);
          }}
          onPrevious={() => {
            const currentIndex = filteredItems.findIndex(i => i.id === selectedItemId);
            const prevIndex = (currentIndex - 1 + filteredItems.length) % filteredItems.length;
            setSelectedItemId(filteredItems[prevIndex].id);
          }}
        />
      )}
    </div>
  );
};

export default GalleryView;
